import { useState } from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import { GoogleOAuthProvider, GoogleLogin, CredentialResponse } from '@react-oauth/google'
import api from '../services/api'
import { useAuth } from '../contexts/AuthContext'

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || ''

function LoginContent() {
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { login } = useAuth()

  // Get return URL from navigation state
  const from = (location.state as any)?.from || '/'

  // Handle Google login with ID token (credential) from GoogleLogin component
  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    try {
      setLoading(true)
      setError('')

      console.log('🔐 Logging in with Google...')

      // credentialResponse.credential is the ID token (JWT)
      const idToken = credentialResponse.credential

      if (!idToken) {
        setError('Failed to get Google credentials')
        return
      }

      console.log('📧 Got Google ID token')

      // Send ID token to backend for verification
      const result = await api.loginWithGoogle(idToken, {})

      if (result.success && result.token && result.user) {
        console.log('✅ Login successful')

        // Update auth context
        login(result.token, result.user)

        // ✅ STANDARDIZED: Trust backend's profileCompleted flag as the single source of truth
        const user = result.user
        const isProfileComplete = user?.profileCompleted === true || user?.profileComplete === true

        console.log('📊 Profile check:', {
          username: user?.username,
          name: user?.name,
          profileCompleted: user?.profileCompleted,
          profileComplete: user?.profileComplete,
          isComplete: isProfileComplete
        })

        if (isProfileComplete) {
          console.log('✅ Profile complete, redirecting to:', from)
          navigate(from, { replace: true })
        } else {
          console.log('⚠️ Profile incomplete, redirecting to profile setup')
          navigate('/profile-setup', { state: { from } })
        }
      } else {
        setError(result.message || 'Login failed')
      }
    } catch (err) {
      console.error('Login error:', err)
      setError('An error occurred during login')
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleError = () => {
    setError('Google Sign-In was cancelled or failed')
    setLoading(false)
  }

  const handleAppleLogin = async () => {
    setLoading(true)
    setError('')

    try {
      // Apple Sign-In for Web
      // @ts-ignore - AppleID is loaded from Apple's CDN
      if (typeof AppleID !== 'undefined') {
        // @ts-ignore
        const data = await AppleID.auth.signIn()

        console.log('🍎 Logging in with Apple...', data)

        const result = await api.loginWithApple(
          data.authorization.code,
          data.authorization.id_token,
          data.user
        )

        if (result.success && result.token && result.user) {
          console.log('✅ Apple login successful')

          // Update auth context
          login(result.token, result.user)

          const user = result.user
          const isProfileComplete = user?.profileCompleted === true || user?.profileComplete === true

          if (isProfileComplete) {
            console.log('✅ Profile complete, redirecting to:', from)
            navigate(from, { replace: true })
          } else {
            console.log('⚠️ Profile incomplete, redirecting to profile setup')
            navigate('/profile-setup', { state: { from } })
          }
        } else {
          setError(result.message || 'Apple login failed')
        }
      } else {
        setError('Apple Sign-In is not available. Please use Google or Email/Phone.')
      }
    } catch (err: any) {
      console.error('Apple login error:', err)
      if (err.error === 'popup_closed_by_user') {
        // User closed the popup - don't show error
        setError('')
      } else {
        setError('An error occurred during Apple Sign-In')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:flex-1 bg-onrecord-orange items-center justify-center">
        <div className="text-white">
          <img
            src="/logo.svg"
            alt="on record"
            className="w-64 h-64 mx-auto mb-8"
          />
          <h1 className="text-5xl font-bold text-center">on record</h1>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <div className="mb-8">
              <img
                src="/logo.svg"
                alt="on record"
                className="w-12 h-12 mb-4 lg:hidden"
              />
              <h1 className="text-5xl font-bold mb-8">Trust what you see</h1>
              <h2 className="text-3xl font-bold mb-8">Join today.</h2>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                {error}
              </div>
            )}

            {/* Social Login Buttons */}
            <div className="space-y-3 mb-4">
              {/* Google Sign-In with ID Token (proper JWT for backend verification) */}
              <div className="relative w-full" style={{ height: 48 }}>
                {/* Hidden Google button — click passes through from custom button */}
                <div className="absolute inset-0 opacity-0 overflow-hidden" style={{ zIndex: 2 }}>
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    useOneTap={false}
                    theme="outline"
                    size="large"
                    width={448}
                    text="signin_with"
                    shape="rectangular"
                  />
                </div>
                {/* Custom styled button — visually matches Apple/OTP */}
                <button
                  type="button"
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  style={{ pointerEvents: 'none' }}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <span className="text-gray-700 font-medium">Sign in with Google</span>
                </button>
              </div>

              <button
                onClick={handleAppleLogin}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                Sign in with Apple
              </button>

              <Link
                to="/login-otp"
                className="w-full flex items-center justify-center px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Sign in with OTP
              </Link>
            </div>

            <div className="mt-4 text-center">
              <Link
                to="/forgot-password"
                className="text-sm text-gray-600 hover:text-onrecord-orange hover:underline"
              >
                Forgot password?
              </Link>
            </div>

            <div className="mt-8 text-gray-500 text-sm">
              Don't have an account?{' '}
              <Link to="/signup" className="text-onrecord-orange hover:underline">
                Sign up
              </Link>
            </div>

            {/* Verify Content Box */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500 mb-3 text-center">Verify OnRecord content</p>
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  const code = (e.currentTarget.elements.namedItem('code') as HTMLInputElement)?.value?.trim()
                  if (code) window.location.href = `/verify/${code.toUpperCase()}`
                }}
                className="flex gap-2"
              >
                <input
                  name="code"
                  type="text"
                  placeholder="Enter verification code"
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-mono tracking-wider focus:outline-none focus:border-onrecord-orange focus:ring-1 focus:ring-onrecord-orange"
                  maxLength={16}
                />
                <button
                  type="submit"
                  className="px-4 py-2.5 bg-onrecord-orange hover:bg-onrecord-orange-dark text-white font-medium text-sm rounded-lg transition-colors"
                >
                  Verify
                </button>
              </form>
            </div>

            {/* Footer Links */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex flex-wrap justify-center gap-x-3 gap-y-2 text-xs text-gray-500">
                <a href="/about" className="hover:underline">About</a>
                <a href="/terms" className="hover:underline">Terms</a>
                <a href="/privacy" className="hover:underline">Privacy</a>
                <a href="/cookies" className="hover:underline">Cookies</a>
              </div>
              <div className="mt-2 text-center text-xs text-gray-500">
                &copy; 2026 on record
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

export default function Login() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <LoginContent />
    </GoogleOAuthProvider>
  )
}
